import { DiscussionCreate, QUERY_KEYS } from "@cnx/common";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import i18n from "common/i18n";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import DiscussionService from "services/DiscussionService/DiscussionService";
import { showError } from "utils/index";

export const useDiscussion = ({
  discussionId,
  enabled = true,
}: {
  discussionId?: string;
  enabled?: boolean;
}) => {
  const { t } = useTranslation("discussions");
  const { error, data, isPending, isFetching, isFetched, isSuccess } = useQuery(
    {
      queryKey: [QUERY_KEYS.DISCUSSIONS, discussionId],

      queryFn: () => {
        if (discussionId) {
          return DiscussionService.fetchDiscussionById({ discussionId });
        }
      },
      enabled: enabled && Boolean(discussionId),
      staleTime: 0,
      placeholderData: undefined,
    }
  );

  useEffect(() => {
    if (!enabled || !error) return;

    showError(t("fetchFailed"), true);
  }, [enabled, error, t]);

  return {
    discussion: data,
    isPending,
    isFetching: isFetching && !isFetched,
    isSuccess,
  };
};

export const useDiscussionContext = ({
  discussionId,
  enabled = true,
}: {
  discussionId: string;
  enabled?: boolean;
}) => {
  const { data, isPending, isSuccess, isFetched, isFetching } = useQuery({
    queryKey: [QUERY_KEYS.DISCUSSION_CONTEXT, discussionId],

    queryFn: () => {
      if (discussionId) {
        return DiscussionService.fetchDiscussionContextByDiscussionId({
          discussionId,
        });
      }
    },
    enabled: enabled && Boolean(discussionId),
    staleTime: 0,
  });

  return {
    discussionContext: data,
    isPending,
    isFetching: isFetching && !isFetched,
    isSuccess,
  };
};

export const useDiscussions = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) => {
  const { t } = useTranslation("discussions");

  const { error, data, isPending, isSuccess } = useQuery({
    queryKey: [QUERY_KEYS.DISCUSSIONS, i18n.language],
    queryFn: DiscussionService.fetchDiscussions,
    enabled,
  });

  useEffect(() => {
    if (!enabled || !error) return;

    showError(t("fetchFailed"), true);
  }, [enabled, error, t]);

  if (!enabled) {
    return {
      discussions: [],
      isPending: false,
      isSuccess: false,
    };
  }

  return { discussions: data ?? [], isPending, isSuccess };
};

export const useDiscussionsMutation = () => {
  const { t } = useTranslation("discussions");
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ body }: { body: DiscussionCreate }) => {
      return DiscussionService.addDiscussion(body);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.DISCUSSIONS],
      });
    },

    onError: () => showError(t("saveFailed")),
  });
  return { mutateAsync, isPending };
};
